import { Modal } from '../../Modal';
import { useTranslation } from '../../../lib/i18n';
import { useAppShare } from '../../../lib/use-app-share';
import { accessLine, shareErrorKey, shareLabel } from '../../../lib/share-flow';
import { APP_SHARE_COPY } from '../../../lib/app-share-flow';
import type { WorkspaceApp } from '../../../lib/app-shares';
import { Badge, Button, Card, TextButton, Typography } from '../ui';
import { LendConnections } from './LendConnections';
import { RecipientField } from '../share/RecipientField';
import { GrantNotices } from '../share/GrantNotices';
import { mergeOutcomes, type PickedRecipient } from '../../../lib/recipient-field';
import type { GrantOutcome } from '../../../lib/share-flow';
import { useState } from 'react';
import styles from './ShareAppDialog.module.css';

// "Share" on a mini-app card: give someone an email address' worth of access, and see/undo who already
// has it.
//
// The only interesting thing on screen is the line under the people list, and it has THREE states, not
// two — `lib/share-flow.ts` decides which, this file only renders it:
//
//   loaded + empty  → "Private" + the hint. We asked, and nobody has access.
//   loaded + people → "Shared with N people", above the rows themselves.
//   FAILED to load  → "Couldn't load who has access" + Try again. We do NOT know, and say so.
//
// A failed read shown as "Private" would be this dialog lying about a permission, which is why the error
// branch is a first-class state all the way down to the API route (see pages/api/app-shares.ts).
export function ShareAppDialog({ app, onClose }: { app: WorkspaceApp; onClose: () => void }) {
  const { t } = useTranslation();
  const flow = useAppShare(app.id, app.requiredTools);
  const { state, shares } = flow;
  const line = accessLine(shares, APP_SHARE_COPY);
  // Picked but not yet shared with. Held here, not in the share machine, because the machine is about
  // ONE attempt and a list of intentions is not an attempt.
  const [picked, setPicked] = useState<PickedRecipient[]>([]);
  // Held for the WHOLE run, not per grant. `state.submitting` goes false between grants, so a second
  // press mid-run would launch a duplicate run over the same list — with the same lending attached.
  const [sending, setSending] = useState(false);
  const [outcomes, setOutcomes] = useState<GrantOutcome[]>([]);

  // Everybody picked, in one press — with the same lending applied to each. What comes back is who
  // did NOT land; they stay on screen so nobody has to remember which of five names went through.
  const share = async (confirmUnknown = false) => {
    if (picked.length === 0 || sending || !flow.decision.canShare) return;
    setSending(true);
    try {
      const result = await flow.submitMany(picked.map((p) => p.identifier), confirmUnknown);
      setPicked((current) => current.filter((p) => result.failed.includes(p.identifier)));
      setOutcomes((previous) => mergeOutcomes(previous, result.outcomes));
    } finally {
      setSending(false);
    }
  };

  const busy = state.submitting || sending;

  // Editing the list retires the LAST attempt's verdict. A red "couldn't share this" describes a
  // press that is no longer what the screen says, and leaving it up while somebody fixes the list
  // makes it read as a problem with what they are fixing. Both phones already did this; web was
  // relying on `flow.setEmail`, which the field replaced. The landed grants' notices stay — those
  // are about shares that really happened.
  const editPicked = (next: PickedRecipient[]) => {
    setPicked(next);
    if (state.error) flow.clearError();
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="minimum"
      maxHeightPx={650}
      scrollBody
      headerSeparator
      title={t('apps.share.title')}
      description={app.name}
      footer={
        state.confirmEmail ? (
          // The question replaces the Share button rather than sitting beside it: nothing was written,
          // and leaving the original action live invites the owner to press it again and get asked
          // again. Cancel is NOT destructive-styled — declining to share with a stranger is an
          // ordinary answer, not damage.
          <div className={styles.confirmActions}>
            <Button variant="outlined" disabled={busy} onClick={() => flow.cancelConfirm()}>
              {t('apps.share.confirmUnknown.cancel')}
            </Button>
            {/* Answering resumes the whole list from where it stopped — the person being asked
                about is still the first one on it. */}
            {/* Same two gates as Share: an unanswerable press that silently does nothing is worse
                than a disabled button, and the reason is already on screen above. */}
            <Button loading={busy} disabled={!flow.decision.canShare || picked.length === 0} onClick={() => void share(true)}>
              {t('apps.share.confirmUnknown.confirm')}
            </Button>
          </div>
        ) : (
          <Button
            loading={busy}
            // Blocked while lending is on with nothing ticked. The server refuses that combination, so a
            // button that fired it would report a failure for a mistake the dialog can already see.
            // Blocked with nobody picked for the same reason: there is nothing to share.
            disabled={!flow.decision.canShare || picked.length === 0}
            onClick={() => void share()}
          >
            {t('apps.share.submit')}
          </Button>
        )
      }
    >
      <div className={styles.body}>
        <RecipientField
          // Empty is the only honest input while the read is out or has FAILED — we do not know who
          // holds a grant, and inventing an exclusion list would hide people who do. A duplicate that
          // slips through is a server-side no-op reported as `alreadyShared`, which is why this is the
          // cheap error and claiming knowledge is not.
          alreadyHasAccess={shares.status === 'ready' ? shares.shares.map((s) => s.granteeEmail ?? '').filter(Boolean) : []}
          picked={picked}
          onPickedChange={editPicked}
          disabled={busy}
          errorMessage={
            state.error === 'invalidEmail' || state.error === 'shareFailed' ? t(shareErrorKey(state.error, APP_SHARE_COPY)) : undefined
          }
          onSubmit={() => void share()}
          bi={{ pageName: 'workspace_apps', modalName: 'share_app' }}
        />

        {/* What this share lets the other person do on THIS account, if anything. Off by default and
            per tool: an ordinary share hands over the app and nothing else — whoever opens it connects
            their own Symphony and reaches nobody — so lending is opt-in, and the copy says what off
            means rather than leaving the reader to infer it. */}
        <LendConnections
          lending={flow.lending}
          onLendingChange={flow.setLending}
          lendable={flow.lendable}
          chosen={flow.chosen}
          onChosenChange={flow.setChosen}
          disabled={busy}
          requiredTools={app.requiredTools}
          showAllTools={flow.showAllTools}
          onShowAllToolsChange={flow.setShowAllTools}
        />

        {/* Why Share is blocked, said next to the thing that blocked it. */}
        {flow.decision.blockedKey && (
          <Typography variant="p3" color="secondary" component="p">
            {t(flow.decision.blockedKey)}
          </Typography>
        )}

        {/* Every landed grant's own notices — one block per person. The share machine holds only the
            LAST attempt's, and in a five-person run the notice worth reading is rarely the fifth. */}
        <GrantNotices
          outcomes={outcomes}
          copy={{ invited: 'apps.share.invited', notifyFailed: 'apps.share.notifyFailed', linkHint: 'apps.share.linkHint' }}
        />

        {/* NOTHING HAS BEEN WRITTEN YET. Sharing by email is get-or-create, so a typo and a real
            stranger look identical afterwards — this is the one moment the difference can still be
            acted on, which is why it is a question and not the notice above. */}
        {state.confirmEmail && (
          <Card variant="stone" size="sm" className={styles.confirm}>
            <Typography variant="p2" color="strong" component="p">
              {t('apps.share.confirmUnknown.title', { email: state.confirmEmail })}
            </Typography>
            <Typography variant="p3" color="secondary" component="p">
              {t('apps.share.confirmUnknown.body')}
            </Typography>
          </Card>
        )}

        <div className={styles.people}>
          <Typography variant="h4" color="strong" component="h3">
            {t('apps.share.peopleTitle')}
          </Typography>

          {/* Nothing is claimed while the read is still out — `accessLine` answers null and no line shows. */}
          {line?.kind === 'private' && (
            <div className={styles.line}>
              <Typography variant="p2" color="secondary" component="p">
                {t(line.key)}
              </Typography>
              <Typography variant="p3" color="muted" component="p">
                {t(line.hintKey)}
              </Typography>
            </div>
          )}

          {line?.kind === 'loadFailed' && (
            <div className={styles.retryRow}>
              <Typography variant="p2" color="secondary" component="p">
                {t(line.key)}
              </Typography>
              <TextButton size="small" onClick={() => flow.retry()}>
                {t(line.retryKey)}
              </TextButton>
            </div>
          )}

          {line?.kind === 'shared' && (
            <Typography variant="p2" color="secondary" component="p">
              {line.params ? t(line.key, line.params) : t(line.key)}
            </Typography>
          )}

          {shares.status === 'ready' &&
            shares.shares.map((share) => {
              // A share may carry neither a name nor an email. It is still a grant, so it is still listed
              // and still revocable — the row is labelled rather than dropped.
              const label = shareLabel(share, APP_SHARE_COPY);
              return (
                <div key={share.granteeRecipientId} className={styles.row}>
                  <Typography variant="p2" color="default" component="span" className={styles.rowLabel}>
                    {'text' in label ? label.text : t(label.key)}
                  </Typography>
                  {/* Invited and has-access are different facts, and only this says which. Absent
                      reads as accepted, so an older server renders exactly as it does today rather
                      than marking every existing grant as unanswered. */}
                  {share.accepted === false && (
                    <Badge variant="waiting-on-a-reply" label={t('apps.share.invitedPending')} />
                  )}
                  <TextButton
                    size="small"
                    color="destructive"
                    disabled={state.removing === share.granteeRecipientId}
                    // The id comes off the row that was rendered, so a revoke can only target the person
                    // whose Remove was pressed.
                    onClick={() => void flow.remove(share.granteeRecipientId)}
                  >
                    {t('apps.share.remove')}
                  </TextButton>
                </div>
              );
            })}

          {state.error === 'removeFailed' && (
            <Typography variant="p3" color="secondary" component="p">
              {t(shareErrorKey(state.error, APP_SHARE_COPY))}
            </Typography>
          )}
        </div>
      </div>
    </Modal>
  );
}
